import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CentsCurrencyPipe } from '@common/centsCurrency.pipe';
import { Sites } from '@enums/sites';
import { environment } from '@environments/environment';
import { CartAdditionalItem } from '@models/cart-additional-item';
import { Coupon } from '@models/coupon';
import { Discount } from '@models/discount';
import { ExtraCharge } from '@models/extra-charge';
import { Fee } from '@models/fee';
import { OrderUpsell } from '@models/order-upsell';
import { Test } from '@models/test';
import { HealthLabsOrderService } from '@services/healthlabs-order.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  imports: [NgIf, NgClass, NgFor, CentsCurrencyPipe],
})
export class OrderSummaryComponent implements OnInit, OnDestroy {
  public giftCardAppliedAmount = 0;
  public tests: Test[] = [];
  public extraCharges: ExtraCharge[] = [];
  public isLoading: boolean = false;

  protected storage = inject(StorageService);
  private order = inject(OrderService);
  private subscriptions: Subscription[] = [];

  /**
   * Get the site name.
   */
  public get siteName(): string {
    return environment.domain;
  }

  /**
   * Get the site URL.
   */
  public get testsUrl(): string {
    return environment.siteUrls.tests;
  }

  /**
   * Get the order total.
   */
  public get total(): number {
    return this.order.getTotal() - this.giftCardAppliedAmount;
  }

  /**
   * Gets the order subtotal.
   */
  public get subtotal(): number {
    return this.total - this.order.getExtraChargesTotal() - this.order.getOrderUpsellsTotal();
  }

  /**
   * Get the order fee.
   */
  public get fee(): Fee {
    return this.storage.fee;
  }

  /**
   * Get the order coupon.
   */
  public get coupon(): Coupon {
    return this.storage.coupon;
  }

  /**
   * Get the order discount.
   */
  public get discount(): Discount {
    return this.storage.discount;
  }

  /**
   * Gets the order upsells.
   */
  public get orderUpsells(): OrderUpsell[] {
    return this.order.orderUpsells;
  }

  /**
   * Returns true when the remove tag button should be displayed
   */
  public get shouldDisplayRemoveTag(): boolean {
    return this.isHealthLabs;
  }

  /**
   * Gets the cart additional items with conditional text based on delivery preference.
   */
  public get cartAdditionalItems(): CartAdditionalItem[] {
    return this.order.getCartAdditionalItems();
  }

  /**
   * Gets whether the cart summary table headers should be displayed.
   */
  public get displayCartSummaryTableHeaders(): boolean {
    return environment.order.orderSummary.displayCartSummaryTableHeaders;
  }

  /**
   * Gets the text that should precede the name of the tests in the cart.
   */
  public get testsPrefix(): string {
    return environment.order.orderSummary.testsPrefix;
  }

  /**
   * Get the coupon item name which will be displayed in order summary section
   */
  public get couponItemName(): string {
    return (
      this.coupon.coupon_name +
      (environment.order.orderSummary.showCouponCode ? ' (' + this.coupon.coupon_code + ')' : '')
    );
  }

  /**
   * Whether the order summary edit button should be displayed.
   */
  public get shouldDisplayEditButton(): boolean {
    return !this.storage.free && environment.order.orderSummary.allowEditingCart;
  }

  /**
   * The text to be displayed on the edit cart button.
   */
  public get editCartButtonText(): string {
    return environment.order.orderSummary.editCartButtonText;
  }

  /**
   * Returns true when has partner checkbox is checked
   */
  public get hasPartner(): boolean {
    return this.storage.hasPartner;
  }

  /**
   * Returns true when the multiple discount alert should be displayed
   */
  public get shouldDisplayMultipleDiscountAlert(): boolean {
    return this.isHealthLabs && this.tests?.length === 1 && !this.storage.baOrder?.isActive;
  }

  /**
   * Gets the amount of cents a in home collection costs.
   */
  public get inHomeCollectionCostInCents(): number {
    return environment.ngFindalab.inHomeCollectionCostInCents;
  }

  /**
   * Whether the current site is HealthLabs.
   */
  public get isHealthLabs(): boolean {
    return (environment.site as Sites) === Sites.Healthlabs;
  }

  /**
   * Initialize the component.
   */
  public ngOnInit(): void {
    this.tests = this.storage.tests;
    this.order.setPhysicianFeeExtraCharge();
    this.extraCharges = this.order.extraCharges;
    this.subscriptions.push(
      this.storage.testsSubject.subscribe((tests: Test[]) => (this.tests = tests)),
      this.order.giftCardAppliedSubject$.subscribe((amount: number) => this.onGiftCardApplied(amount)),
    );
  }

  /**
   * Unsubscribe on component destroy.
   */
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Get the coupon total amount
   */
  public getTotalCoupons(): number {
    return this.order.getTotalCoupons(this.order.getTestsTotal() + this.order.getPartnerTotal());
  }

  /**
   * Callback when the gift card applied subject is fired.
   *
   * @param {number} appliedTotal the amount of the gift card that is applied
   */
  public onGiftCardApplied(appliedTotal: number): void {
    this.giftCardAppliedAmount = appliedTotal;
  }

  /**
   * Trigger action to remove a test from the cart on HL
   *
   * @param {Test} test the test to be removed
   */
  public removeTest(test: Test): void {
    this.toggleLoading(true);
    (this.order as HealthLabsOrderService).removeTestFromCart(test.entry_id).subscribe({
      next: () => {
        if (this.tests.length === 1) {
          this.toggleLoading(false);
          this.order.clearLastOrder();
          window.location.href = environment.siteUrls.tests;

          return;
        }

        this.order.loadCart([], { hasInHomeCollection: this.storage.hasInHomeCollection }).subscribe({
          next: () => this.toggleLoading(false),
          error: () => this.toggleLoading(false),
        });
      },
      error: () => this.toggleLoading(false),
    });
  }

  /**
   * Toggle loading state.
   *
   * @param {boolean} isLoading Loading state.
   */
  public toggleLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
    this.order.placeOrderButtonDisable = isLoading;
  }

  /**
   * Set display modal state variable to true when the multiple discount modal should be displayed
   */
  public displayFindTestsModal(): void {
    if (!environment.order.orderSummary.displayFindTestsModal) {
      return;
    }

    (this.order as HealthLabsOrderService).findTestsModalState = true;
  }
}
